import React, { type PropsWithChildren } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Platform, ScrollView, StyleSheet } from 'react-native';
import { type Edge, SafeAreaView } from 'react-native-safe-area-context';

import { getIosModalPresentationConfig, IconLink, Modal, theme, useResponsive } from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

export const LoyaltyPostPurchaseContainer = (props: LoyaltyPostPurchaseContainerProps) => {
  const { visible, children, isPremium, onRequestClose, onCloseButtonPress } = props;

  const { formatMessage } = useIntl();
  const { match } = useResponsive();

  return (
    <Modal
      size={match(['full', 'medium'])}
      fitHeight
      visible={visible}
      onRequestClose={onRequestClose}
      accessibilityLabel={formatMessage(messages.modalA11yLabel)}
      palette={isPremium ? 'dark-kale' : 'forest'}
      withSafeAreaView={false}
      {...getIosModalPresentationConfig()}
    >
      <SafeAreaView style={styles.closeButtonContainer} edges={CLOSE_BUTTON_SAFE_AREA_EDGES}>
        <IconLink
          name="IconClose"
          palette="light-semi-transparent"
          width={40}
          height={40}
          accessibilityLabel={formatMessage(messages.closeA11yLabel)}
          onPress={onCloseButtonPress}
        />
      </SafeAreaView>

      <ScrollView
        style={styles.scrollContainer}
        contentContainerStyle={match([undefined, styles.contentContainerSM])}
      >
        <SafeAreaView edges={CONTENT_SAFE_AREA_EDGES} style={styles.container}>
          {children}
        </SafeAreaView>
      </ScrollView>
    </Modal>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  modalA11yLabel: {
    defaultMessage: 'Loyalty offers modal',
    description: 'Loyalty Post Purchase | Modal | a11y label',
  },
  closeA11yLabel: {
    defaultMessage: 'Close modal',
    description: 'Loyalty Post Purchase | Modal | Close button a11y label',
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const CONTENT_SAFE_AREA_EDGES: Edge[] = ['bottom'];
const CLOSE_BUTTON_SAFE_AREA_EDGES: Edge[] = Platform.select({
  android: ['top'],
  default: [],
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollContainer: {
    flex: 1,
  },
  contentContainerSM: {
    flexDirection: 'row',
  },
  closeButtonContainer: {
    position: 'absolute',
    top: theme.spacing['4'],
    right: theme.spacing['4'],
    zIndex: 1,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type LoyaltyPostPurchaseContainerProps = PropsWithChildren<{
  visible: boolean;
  isPremium?: boolean;
  onCloseButtonPress: () => void;
  onRequestClose: () => void;
}>;
